/**
 * Kifoni — Módulo Klarna de la ficha de producto (PROPUESTA / PREVIEW).
 *
 * Qué hace
 *   1. Muestra bajo el precio el mensaje "Paga en 3 plazos de X € con Klarna".
 *   2. Recalcula la cuota al cambiar de variante, escuchando el evento propio
 *      `kf:variant-change` que ya emite assets/kifoni-product.js (fuente única
 *      del precio: variant.price en céntimos; nunca compare_at_price).
 *   3. Si el placement oficial de Klarna On-site Messaging está presente en la
 *      página, actualiza su `data-purchase-amount` y pide el refresco oficial;
 *      el texto nativo queda como fallback documentado.
 *   4. Abre un modal (bottom sheet en móvil) explicativo, sin formularios.
 *
 * Qué NO hace
 *   - No crea ningún botón de pago de Klarna (Express Checkout no está
 *     disponible en Shopify a día de hoy — ver informe).
 *   - No redacta condiciones legales propias: cita el texto oficial de Klarna
 *     y enlaza a su página.
 *   - No toca el formulario de producto, ni el input de variante, ni los CTA.
 *   - Si algo falla, se oculta en silencio: la compra nunca se bloquea.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-kf-klarna]';
  var EVENT = 'kf:variant-change';
  var REFRESH_DEBOUNCE_MS = 160;
  var OSM_RENDER_TIMEOUT_MS = 2500;

  /* ---------------------------------------------------------------------
     Cálculo — enteros de céntimos, sin coma flotante
     --------------------------------------------------------------------- */

  /**
   * Divide un importe en n cuotas trabajando SIEMPRE en céntimos enteros.
   * Devuelve la cuota a mostrar y si el reparto es exacto.
   * Cuando no es exacto se redondea hacia arriba (nunca se muestra una cuota
   * menor de la que el cliente podría pagar) y se marca como orientativa;
   * el total real no se altera en ningún caso.
   */
  function splitInstalment(totalCents, n) {
    var total = Math.round(Number(totalCents) || 0);
    var parts = Math.max(2, parseInt(n, 10) || 3);
    var base = Math.floor(total / parts);
    var remainder = total - base * parts;
    return {
      total: total,
      parts: parts,
      each: remainder === 0 ? base : base + 1,
      exact: remainder === 0
    };
  }

  /**
   * I18N-FR — locale de PRESENTACIÓN de la página.
   *
   * Ojo, no confundir con cfg.locale: ese es el locale CONTRATADO con Klarna
   * que viaja al placement oficial (data-locale) y lo decide el negocio, no el
   * navegante. Este de aquí solo decide cómo se escriben el número y la fecha
   * del mensaje nativo de fallback. Sale de <html lang> —que layout/theme.liquid
   * rellena con request.locale.iso_code— y de Shopify.locale, que llega en
   * content_for_header.
   *
   * En español no cambia nada: 'es' y 'es-ES' dan el mismo resultado, también
   * con importes de 4 cifras (1234,50, sin separador de millar) y con la fecha
   * corta ("14 ago").
   */
  function localeTag() {
    var docLang = document.documentElement ? document.documentElement.lang : '';
    var shopLocale = window.Shopify && window.Shopify.locale;
    return String(docLang || shopLocale || '').trim() || 'es-ES';
  }

  var eurFormatter = null;
  var eurFormatterTag = null;
  function formatEur(cents) {
    var tag = localeTag();
    if (!eurFormatter || eurFormatterTag !== tag) {
      var opts = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
      try {
        eurFormatter = new Intl.NumberFormat(tag, opts);
      } catch (e) {
        eurFormatter = new Intl.NumberFormat('es-ES', opts);
        tag = 'es-ES';
      }
      eurFormatterTag = tag;
    }
    // Formato Kifoni: importe + espacio + símbolo ("51,33 €"), nunca "€51.33".
    return eurFormatter.format(cents / 100) + ' €';
  }

  var dateFormatter = null;
  var dateFormatterTag = null;
  function formatDate(date) {
    var tag = localeTag();
    if (!dateFormatter || dateFormatterTag !== tag) {
      var opts = { day: 'numeric', month: 'short' };
      try {
        dateFormatter = new Intl.DateTimeFormat(tag, opts);
      } catch (e) {
        dateFormatter = new Intl.DateTimeFormat('es-ES', opts);
        tag = 'es-ES';
      }
      dateFormatterTag = tag;
    }
    return dateFormatter.format(date);
  }

  function addDays(date, days) {
    var d = new Date(date.getTime());
    d.setDate(d.getDate() + days);
    return d;
  }

  /* ---------------------------------------------------------------------
     Elegibilidad
     --------------------------------------------------------------------- */

  function readConfig(root) {
    var d = root.dataset;
    return {
      instalments: parseInt(d.instalments, 10) || 3,
      minAmount: d.minAmount === '' || d.minAmount == null ? null : parseInt(d.minAmount, 10),
      maxAmount: d.maxAmount === '' || d.maxAmount == null ? null : parseInt(d.maxAmount, 10),
      country: (d.country || 'ES').toUpperCase(),
      currency: (d.currency || 'EUR').toUpperCase(),
      locale: d.locale || 'es-ES',
      useOsm: d.osm !== 'false',
      showMore: d.more !== 'false'
    };
  }

  /**
   * El país/moneda reales mandan sobre la configuración del theme: si la tienda
   * sirve otro mercado, el módulo no se muestra. Con On-site Messaging la
   * elegibilidad final (y los importes mínimo/máximo reales) los decide Klarna:
   * su placement sencillamente no pinta nada si no aplica.
   */
  function isEligible(cfg, cents, available) {
    if (!cents || cents <= 0) return false;
    if (available === false) return false;
    var shop = window.Shopify || {};
    var currency = (shop.currency && shop.currency.active) || cfg.currency;
    var country = shop.country || cfg.country;
    if (String(currency).toUpperCase() !== cfg.currency) return false;
    if (String(country).toUpperCase() !== cfg.country) return false;
    if (cfg.minAmount != null && cents < cfg.minAmount) return false;
    if (cfg.maxAmount != null && cents > cfg.maxAmount) return false;
    return true;
  }

  /* ---------------------------------------------------------------------
     On-site Messaging oficial (ruta de producción)
     --------------------------------------------------------------------- */

  function refreshPlacements() {
    try {
      if (window.Klarna && window.Klarna.OnsiteMessaging &&
          typeof window.Klarna.OnsiteMessaging.refresh === 'function') {
        window.Klarna.OnsiteMessaging.refresh();
        return true;
      }
      // API heredada (Klarna la mantiene por compatibilidad).
      if (window.KlarnaOnsiteService && typeof window.KlarnaOnsiteService.push === 'function') {
        window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
        return true;
      }
    } catch (e) {
      /* fallo silencioso: nos quedamos con el mensaje nativo */
    }
    return false;
  }

  function placementHasRendered(placement) {
    if (!placement) return false;
    if (placement.shadowRoot && placement.shadowRoot.childElementCount > 0) return true;
    return placement.childElementCount > 0;
  }

  /* ---------------------------------------------------------------------
     Modal / bottom sheet (uno por documento, creado bajo demanda)
     --------------------------------------------------------------------- */

  var modal = null;

  var CLOSE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
    'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function buildModal() {
    var el = document.createElement('div');
    el.className = 'kf-klarna-modal';
    el.setAttribute('hidden', '');
    el.innerHTML =
      '<div class="kf-klarna-modal__dialog" id="kf-klarna-dialog" role="dialog" aria-modal="true" ' +
        'aria-labelledby="kf-klarna-modal-title" tabindex="-1">' +
        '<span class="kf-klarna-modal__grip" aria-hidden="true"></span>' +
        '<button type="button" class="kf-klarna__close" data-kf-klarna-close ' +
          'aria-label="Cerrar">' + CLOSE_ICON + '</button>' +
        '<div class="kf-klarna-modal__head">' +
          '<span class="kf-klarna__badge" aria-hidden="true">Klarna</span>' +
          '<h2 class="kf-klarna-modal__title" id="kf-klarna-modal-title">Cómo funciona pagar en 3 plazos</h2>' +
        '</div>' +
        '<p class="kf-klarna-modal__lead">Divides el importe de tu compra en 3 pagos. ' +
          'El primero se cobra al enviarse el pedido y los dos siguientes cada 30 días.</p>' +
        '<ol class="kf-klarna-steps" data-kf-klarna-steps></ol>' +
        '<p class="kf-klarna-total" data-kf-klarna-total></p>' +
        '<ul class="kf-klarna-list">' +
          '<li>El importe que ves se calcula a partir del precio actual del producto en esta ficha.</li>' +
          '<li>Klarna aparece como opción de pago en la pantalla de pago cuando la compra y el cliente son elegibles.</li>' +
          '<li>Las condiciones definitivas y la disponibilidad se confirman durante el pago.</li>' +
        '</ul>' +
        '<p class="kf-klarna-legal" data-kf-klarna-legal></p>' +
        '<a class="kf-klarna__official" data-kf-klarna-official ' +
          'href="https://www.klarna.com/es/formas-de-pago/paga-en-3-plazos/" ' +
          'target="_blank" rel="noopener noreferrer">' +
          'Información oficial de Klarna sobre «Paga en 3 plazos»</a>' +
      '</div>';
    document.body.appendChild(el);

    el.addEventListener('click', function (evt) {
      if (evt.target === el || evt.target.closest('[data-kf-klarna-close]')) closeModal();
    });

    return el;
  }

  var lastFocused = null;

  function focusables(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
  }

  function onKeydown(evt) {
    if (!modal || modal.hidden) return;
    if (evt.key === 'Escape') {
      evt.stopPropagation();
      closeModal();
      return;
    }
    if (evt.key !== 'Tab') return;
    var dialog = modal.querySelector('.kf-klarna-modal__dialog');
    var items = focusables(dialog);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (evt.shiftKey && document.activeElement === first) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  function openModal(trigger, state) {
    if (!modal) modal = buildModal();
    renderModalContent(state);

    lastFocused = trigger || document.activeElement;
    modal.hidden = false;
    // Reflow forzado antes de abrir: la transición arranca desde el estado
    // cerrado y, sobre todo, el diálogo ya es visible (no visibility:hidden)
    // cuando le damos el foco — si no, focus() se pierde en silencio.
    void modal.offsetHeight;
    modal.classList.add('is-open');
    document.documentElement.classList.add('kf-klarna-scroll-lock');
    document.addEventListener('keydown', onKeydown, true);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    var dialog = modal.querySelector('.kf-klarna-modal__dialog');
    dialog.scrollTop = 0;
    dialog.focus();
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('kf-klarna-scroll-lock');
    document.removeEventListener('keydown', onKeydown, true);

    var finish = function () { modal.hidden = true; };
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) finish();
    else window.setTimeout(finish, 200);

    document.querySelectorAll('[data-kf-klarna-more][aria-expanded="true"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function renderModalContent(state) {
    var steps = modal.querySelector('[data-kf-klarna-steps]');
    var total = modal.querySelector('[data-kf-klarna-total]');
    var legal = modal.querySelector('[data-kf-klarna-legal]');
    var split = state.split;
    var today = new Date();
    var whens = ['Al enviarse el pedido', 'A los 30 días', 'A los 60 días'];
    var offsets = [0, 30, 60];

    var html = '';
    for (var i = 0; i < split.parts; i++) {
      html +=
        '<li class="kf-klarna-step">' +
          '<span class="kf-klarna-step__dot" aria-hidden="true"></span>' +
          '<span class="kf-klarna-step__amount">' + formatEur(split.each) + '</span>' +
          '<span class="kf-klarna-step__when">' + (whens[i] || ('Pago ' + (i + 1))) + '</span>' +
          '<span class="kf-klarna-step__date">' +
            (i === 0 ? 'hoy, ' : '') + formatDate(addDays(today, offsets[i] || i * 30)) +
          '</span>' +
        '</li>';
    }
    steps.innerHTML = html;

    total.innerHTML = split.exact
      ? 'Total de la compra: <strong>' + formatEur(split.total) + '</strong> · ' +
        split.parts + ' pagos de <strong>' + formatEur(split.each) + '</strong>.'
      : 'Total de la compra: <strong>' + formatEur(split.total) + '</strong>. ' +
        'Las cuotas mostradas son orientativas: el reparto exacto de los céntimos ' +
        'lo determina Klarna al confirmar el pago. El total no cambia.';

    // Texto oficial de Klarna (klarna.com/es), citado literalmente.
    legal.textContent =
      'Sujeto a la aprobación de Klarna. Importe mínimo y máximo varía por tienda. ' +
      'Solo es válido para residentes en España y mayores de 18 años. ' +
      'Klarna realiza una verificación como parte de la evaluación de elegibilidad.';
  }

  /* ---------------------------------------------------------------------
     Instancia del módulo
     --------------------------------------------------------------------- */

  function initModule(root) {
    if (!root || root.dataset.kfKlarnaReady === 'true') return;
    root.dataset.kfKlarnaReady = 'true';

    var cfg = readConfig(root);
    var textEl = root.querySelector('[data-kf-klarna-text]');
    var leadEl = root.querySelector('[data-kf-klarna-lead]');
    var amountEl = root.querySelector('[data-kf-klarna-amount]');
    var moreBtn = root.querySelector('[data-kf-klarna-more]');
    var placement = root.querySelector('klarna-placement');
    var note = document.querySelector('[data-kf-klarna-note]');
    var sectionId = root.getAttribute('data-section-id') || '';

    var state = {
      cents: parseInt(root.dataset.amount, 10) || 0,
      available: root.dataset.available !== 'false',
      split: splitInstalment(parseInt(root.dataset.amount, 10) || 0, cfg.instalments)
    };

    var refreshTimer = null;
    var renderTimer = null;
    var lastAnnounced = '';

    if (!cfg.showMore && moreBtn) moreBtn.hidden = true;

    function setVisible(visible) {
      root.hidden = !visible;
      if (note) note.hidden = !visible;
    }

    function paint() {
      var split = state.split;
      var sentence = split.exact
        ? 'Paga en ' + split.parts + ' plazos de '
        : 'Paga en ' + split.parts + ' plazos de aprox. ';

      // La región aria-live="polite" se declara en el marcado y no se toca nunca:
      // así el lector de pantalla la tiene registrada antes de cualquier cambio.
      // Para no generar anuncios repetidos al navegar entre variantes del mismo
      // precio, simplemente NO se escribe en el DOM si la frase no ha cambiado.
      var announced = sentence + formatEur(split.each);
      if (announced === lastAnnounced) return;
      lastAnnounced = announced;

      if (leadEl) leadEl.textContent = sentence;
      if (amountEl) amountEl.textContent = formatEur(split.each);
    }

    function osmLibraryPresent() {
      return !!(window.Klarna && window.Klarna.OnsiteMessaging) || !!window.KlarnaOnsiteService;
    }

    function syncPlacement() {
      if (!cfg.useOsm || !placement) return;
      // Sin librería oficial cargada no hay nada que refrescar: nos quedamos en
      // el mensaje nativo (fallback) y no mostramos estado de carga alguno.
      if (!osmLibraryPresent()) {
        root.classList.remove('is-refreshing');
        placement.setAttribute('data-purchase-amount', String(state.cents));
        return;
      }
      // data-purchase-amount va en unidades menores (céntimos) y SIN moneda:
      // la moneda la deduce Klarna del locale.
      placement.setAttribute('data-purchase-amount', String(state.cents));
      placement.setAttribute('data-locale', cfg.locale);

      root.classList.add('is-refreshing');
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(function () {
        var asked = refreshPlacements();
        window.clearTimeout(renderTimer);
        renderTimer = window.setTimeout(function () {
          root.classList.remove('is-refreshing');
          // Si el placement oficial ha pintado, el mensaje nativo se retira
          // para no duplicar; si no, se queda como fallback visual.
          var rendered = asked && placementHasRendered(placement);
          root.classList.toggle('kf-klarna--osm-active', !!rendered);
          if (textEl) textEl.hidden = !!rendered;
          var badge = root.querySelector('.kf-klarna__badge');
          if (badge) badge.hidden = !!rendered;
        }, OSM_RENDER_TIMEOUT_MS);
      }, REFRESH_DEBOUNCE_MS);
    }

    function update(cents, available) {
      var next = Math.round(Number(cents) || 0);
      state.cents = next;
      state.available = available !== false;
      state.split = splitInstalment(next, cfg.instalments);

      if (!isEligible(cfg, next, state.available)) {
        setVisible(false);
        return;
      }
      setVisible(true);
      paint();
      syncPlacement();
    }

    // Estado inicial (renderizado en servidor por Liquid; aquí solo se valida).
    update(state.cents, state.available);

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        openModal(moreBtn, state);
      });
    }

    // Un único listener por módulo. El evento burbujea hasta document, así que
    // filtramos por sección para soportar varias fichas en la misma página.
    document.addEventListener(EVENT, function (evt) {
      var origin = evt.target;
      if (sectionId && origin && origin.getAttribute &&
          origin.getAttribute('data-section-id') &&
          origin.getAttribute('data-section-id') !== sectionId) {
        return;
      }
      var variant = evt.detail && evt.detail.variant;
      if (!variant) { setVisible(false); return; }
      update(variant.price, variant.available);
    });

    // API mínima para el panel de revisión del preview y para depuración.
    root.kfKlarna = { update: update, state: state, config: cfg, split: splitInstalment };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(initModule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme Editor: al recargar la sección se reinicializa sin duplicar listeners
  // (el flag data-kf-klarna-ready viaja con el nodo nuevo, que aún no lo tiene).
  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });

  window.KifoniKlarna = {
    init: initAll,
    splitInstalment: splitInstalment,
    formatEur: formatEur,
    closeModal: closeModal
  };
})();
